import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material'
import { ExplanationAccordion } from 'components'
import { CompanyInsuranceCompanyInformationDto, companyInsuranceCompanyInformationValidationSchema } from 'dtos'
import { Formik } from 'formik'

interface InsuranceCompanyAddEditDialogProps {
  initialValues: CompanyInsuranceCompanyInformationDto
  loading: boolean
  onClose: () => void
  onSave: (values: any) => void
  open: boolean
}

export default function InsuranceCompanyAddEditDialog({
  initialValues,
  loading,
  onClose,
  onSave,
  open
}: InsuranceCompanyAddEditDialogProps) {
  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={companyInsuranceCompanyInformationValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values, validateField }) => {
          const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(e)
            validateField(e.target.name)
          }

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Insurance Company</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.insuranceUnderwriterCompanyName && errors.insuranceUnderwriterCompanyName)}
                      helperText={touched.insuranceUnderwriterCompanyName && errors.insuranceUnderwriterCompanyName}
                      label='Underwriter Company Name'
                      name='insuranceUnderwriterCompanyName'
                      onBlur={e => {
                        handleBlur(e)
                        validateField(e.target.name)
                      }}
                      onChange={handleFieldChange}
                      value={values.insuranceUnderwriterCompanyName}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.insuranceSuretyCode && errors.insuranceSuretyCode)}
                      helperText={touched.insuranceSuretyCode && errors.insuranceSuretyCode}
                      label='Surety Code'
                      name='insuranceSuretyCode'
                      onBlur={e => {
                        handleBlur(e)
                        validateField(e.target.name)
                      }}
                      onChange={handleFieldChange}
                      value={values.insuranceSuretyCode}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <input
                      accept='.png,.jpg,.jpeg'
                      hidden
                      type='file'
                      id='insuranceSuretyImageSealFileInput'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('insuranceSuretyImageSealFile', e.target.files[0])
                          validateField('insuranceSuretyImageSealFile')
                        }
                      }}
                    />

                    <Button
                      disabled={loading || isSubmitting}
                      onClick={() => {
                        document.getElementById('insuranceSuretyImageSealFileInput')?.click()
                      }}
                      variant='contained'
                    >
                      UPLOAD SURETY IMAGE SEAL
                    </Button>

                    <Typography color='#808080' display='inline' variant='body2' sx={{ ml: 1 }}>
                      {values.insuranceSuretyImageSealFile?.name ??
                        values.insuranceSuretyImageSealFileName ??
                        'No file uploaded'}
                    </Typography>

                    {errors.insuranceSuretyImageSealFile && (
                      <Typography color='error' variant='body2'>
                        {errors.insuranceSuretyImageSealFile}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      ||All fields are required.||Surety Seal Image must be uploaded to save.||Image formats accepted: .PNG,
                      .JPG or .JPEG.||The maximum allowed file size for image upload is 20 MB. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={loading || isSubmitting} color='inherit' onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
